import * as pdfjsLib from 'pdfjs-dist'

import { PdfLinkAnnotation, PdfLinkOverlay, PdfOutlineItem } from './pdf-viewer.types'

let workerConfigured = false

function ensurePdfWorker() {
  if (workerConfigured || typeof window === 'undefined') {
    return
  }

  pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`
  workerConfigured = true
}

export async function loadPdfDocument(url: string) {
  ensurePdfWorker()
  const loadingTask = pdfjsLib.getDocument({ url, withCredentials: false })
  const pdfDocument = await loadingTask.promise
  return { loadingTask, pdfDocument }
}

export async function getPdfOutline(pdfDocument: pdfjsLib.PDFDocumentProxy) {
  const outline = await pdfDocument.getOutline()
  if (!outline || outline.length === 0) {
    return null
  }

  return outline as PdfOutlineItem[]
}

export async function renderPdfPage(params: {
  pdfDocument: pdfjsLib.PDFDocumentProxy
  pageNumber: number
  scale: number
  canvas: HTMLCanvasElement
}) {
  const page = await params.pdfDocument.getPage(params.pageNumber)
  const viewport = page.getViewport({ scale: params.scale })
  const deviceScale = typeof window === 'undefined' ? 1 : Math.min(window.devicePixelRatio || 1, 2)

  params.canvas.width = Math.floor(viewport.width * deviceScale)
  params.canvas.height = Math.floor(viewport.height * deviceScale)
  params.canvas.style.width = `${viewport.width}px`
  params.canvas.style.height = `${viewport.height}px`

  const context = params.canvas.getContext('2d')
  if (!context) {
    throw new Error('Unable to initialize canvas context')
  }

  context.setTransform(1, 0, 0, 1, 0, 0)
  context.clearRect(0, 0, params.canvas.width, params.canvas.height)

  const renderTask = page.render({
    canvasContext: context,
    viewport,
    transform: deviceScale !== 1 ? [deviceScale, 0, 0, deviceScale, 0, 0] : undefined,
  })

  await renderTask.promise

  const annotations = (await page.getAnnotations()) as unknown as PdfLinkAnnotation[]

  return { viewport, annotations }
}

export function buildLinkOverlays(params: { annotations: PdfLinkAnnotation[]; viewport: pdfjsLib.PageViewport }) {
  const overlays: PdfLinkOverlay[] = []

  params.annotations.forEach(annotation => {
    if (!annotation || !Array.isArray(annotation.rect)) {
      return
    }

    const [x1, y1] = params.viewport.convertToViewportPoint(annotation.rect[0], annotation.rect[3])
    const [x2, y2] = params.viewport.convertToViewportPoint(annotation.rect[2], annotation.rect[1])

    overlays.push({
      left: Math.min(x1, x2),
      top: Math.min(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1),
      dest: annotation.dest,
      url: annotation.url,
    })
  })

  return overlays
}

export async function resolvePdfDestinationPage(params: {
  pdfDocument: pdfjsLib.PDFDocumentProxy
  destination: string | unknown[] | null
  totalPages: number
}) {
  const { destination, pdfDocument, totalPages } = params
  if (!destination) {
    return null
  }

  let targetPage: number | null = null

  if (Array.isArray(destination)) {
    const firstToken = destination[0]
    if (typeof firstToken === 'number') {
      targetPage = firstToken >= 1 ? firstToken : firstToken + 1
    } else if (firstToken && typeof firstToken === 'object') {
      const pageIndex = await pdfDocument.getPageIndex(firstToken as never)
      targetPage = pageIndex + 1
    }
  } else if (typeof destination === 'string') {
    const resolvedDest = await pdfDocument.getDestination(destination)
    if (resolvedDest && Array.isArray(resolvedDest) && resolvedDest[0]) {
      const pageRef = resolvedDest[0]
      if (typeof pageRef === 'number') {
        targetPage = pageRef + 1
      } else if (pageRef && typeof pageRef === 'object') {
        const pageIndex = await pdfDocument.getPageIndex(pageRef as never)
        targetPage = pageIndex + 1
      }
    }
  }

  if (!targetPage || Number.isNaN(targetPage)) {
    return null
  }

  if (targetPage < 1 || targetPage > totalPages) {
    return null
  }

  return targetPage
}
